import {
  Box,
  HStack,
  IconButton,
  Link,
  useToken,
  VStack,
} from '@chakra-ui/react';
import { TextLink } from '@time-line/components/common';
import { Close } from '@time-line/components/icons';
import { useAnalytics } from 'hooks/useAnalytics';
import { useToastNotifiations } from 'hooks/useToastNotifications';
import { useImage } from 'lib/sanity';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useEffectOnce } from 'react-use';
import type { ToastNotification } from 'types/cms/ToastNotification';

import LiveStatusResolver from './LiveStatusResolver';

export function Notification({
  notification,
  onClose,
}: {
  notification: ToastNotification;
  onClose: (id: string) => void;
}) {
  const router = useRouter();
  const [blackAlpha100] = useToken('colors', ['blackAlpha.100']);
  const { toggleNotification } = useToastNotifiations();
  const { analytics } = useAnalytics();

  const {
    image = null,
    isEventMode,
    displayTimes,
    title,
    subtitle,
    overline,
    url,
  } = notification;

  const imageProps = useImage(image, { ratio: 92 / 124 });

  useEffectOnce(() => {
    analytics.popupBannerDisplay({
      title,
      url: url || '',
    });
  });

  const handleClose = () => {
    toggleNotification(notification._key, false);
    onClose(notification._key);

    analytics.popupBannerClose({
      title,
      url: url || '',
    });
  };

  const handleClick = () => {
    analytics.popupBannerClick({
      title,
      url: url || '',
    });

    if (url) {
      router.push(url);
    }
  };

  return (
    <HStack
      aria-label="toast notification"
      bg="gray.600"
      borderRadius="xl"
      p="4xs"
      gap="5xs"
      align="stretch"
      position="relative"
      boxShadow={`0px 0px 0px 1px rgba(0, 0, 0, 0.02), 0px 1px 4px 0px ${blackAlpha100}, 0px 6px 24px 0px ${blackAlpha100}`}
    >
      {imageProps && (
        <Box
          position="relative"
          flex="none"
          width={92}
          aspectRatio={isEventMode ? 'auto' : 1}
          borderRadius="base"
          overflow="hidden"
          height="auto"
        >
          <Link
            as="button"
            aria-label={title}
            onClick={handleClick}
            display="block"
            height="100%"
            width="100%"
          >
            <Image
              src={imageProps.src}
              loader={imageProps.loader}
              blurDataURL={imageProps.blurDataURL}
              alt={imageProps.alt}
              placeholder={imageProps.placeholder}
              suppressHydrationWarning
              sizes="100px"
              fill
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
              }}
            />
          </Link>
        </Box>
      )}
      <VStack px="3xs" py="5xs" gap="5xs" align="flex-start" flex="1">
        {displayTimes && <LiveStatusResolver banner={notification} />}
        {overline && (
          <TextLink
            href={url}
            textProps={{
              size: 'detail',
              fontWeight: 'medium',
              color: 'whiteAlpha.700',
            }}
          >
            {overline}
          </TextLink>
        )}
        {title && (
          <TextLink
            textProps={{
              size: 'title2',
              color: 'gray.50',
              fontWeight: 'medium',
              lineHeight: 'base',
            }}
            href={url}
          >
            {title}
          </TextLink>
        )}
        {subtitle && (
          <TextLink
            href={url}
            textProps={{
              size: 'body3',
              color: 'whiteAlpha.700',
            }}
          >
            {subtitle}
          </TextLink>
        )}
      </VStack>
      <IconButton
        position="absolute"
        top="4xs"
        right="4xs"
        p="2px"
        aria-label="Close toast notification"
        onClick={handleClose}
        icon={<Close width="20px" display="block" />}
        display="inline-block"
        verticalAlign="top"
        borderRadius="base"
        _hover={{ color: 'gray.50', bg: 'whiteAlpha.200' }}
      />
    </HStack>
  );
}
